/**
 * Exhaustive structural pattern matching: `match(value).with(pattern, handler)...`.
 *
 * API-compatible with the subset of ts-pattern 5 that StanfordTax uses, rebuilt so the
 * type-level work stays proportional to what a call site asks for. ts-pattern's general
 * machinery (selections anywhere, variadic arrays, sets/maps/records) made `match` the
 * dominant cost of type-checking st-app.
 *
 * Patterns
 * --------
 * A pattern is a literal (compared with `Object.is`), a tuple (same length, element-wise),
 * an object (every listed key present and matching; unlisted keys ignored), or a matcher
 * built by `P` (`P.union`, `P.not`, `P.string`, ...). Matchers carry a runtime predicate
 * under the `matcher` symbol and a phantom `kind` the types dispatch on.
 *
 * Each pattern is checked against `KnownPattern<V>`, the shape of the input: unknown keys
 * and misspelled literals fail where they are written ("Did you mean 'load'?"), and the
 * same type drives editor completion. `P.union`/`P.not`/`P.when` infer their input from
 * their position, so their arguments are checked and completed too. A pattern that fits
 * the shape but matches no remaining case (`{ type: 'load', value: 1 }` when only
 * 'loaded' has a value) is rejected at its handler: it is a typo or a dead arm.
 * `P.Pattern<V>` and `P.valid<V, p>` expose both checks to APIs that accept patterns.
 *
 * Type-level model
 * ----------------
 * A chain `match(v).with(p1, h1).with(p2, h2)...` threads three types:
 *
 *   V  the input still unhandled at the top level. Each `.with` drops the union members
 *      its pattern fully covers (`ExcludeCovered`). Handlers narrow from V, and
 *      `.otherwise` receives it.
 *   H  the union of every pattern seen so far.
 *   R  the union of handler return types (or the `.returnType<O>()` override).
 *
 * `.with` never distributes V into nested cases; that is the expensive part. Only
 * `.exhaustive()` does, and only along the keys the handled patterns mention
 * (`DistributeBy`). Example, with State = idle | loaded{data: a | b}:
 *
 *   match({ state, message })
 *     .with({ message: { type: 'reset' } }, ...)
 *     .with({ state: { status: 'loaded', data: { kind: 'a' } } }, ...)
 *     .exhaustive()
 *
 * distributes to {idle, M} | {loaded a, M} | {loaded b, M} for each message member M and
 * reports every member no single pattern fully covers ({idle, load}, {loaded b, load},
 * ...). `P.when`, number comparisons and the guard in `.with(pattern, guard, handler)`
 * narrow handlers but never count as covering a case, because a runtime predicate can
 * reject any value of its type.
 *
 * Runtime
 * -------
 * The builder evaluates arms in order and remembers the first handler result. A chain that
 * reaches `.exhaustive()` without a match throws: a value the types said was impossible
 * arrived, so it is a bug. `.run()` skips the type-level check but throws the same way.
 */

const matcher = Symbol('@st/pattern/matcher')
declare const kind: unique symbol
declare const narrowsTo: unique symbol
declare const writtenFor: unique symbol
declare const unset: unique symbol
type Unset = typeof unset

type MatchFn = (value: unknown) => boolean

// I is the input a matcher was written against. `P.union`, `P.not` and `P.when` infer it
// from the pattern position they appear in, which is what types their arguments.
interface Matcher<K extends string, T = unknown, I = any> {
  readonly [matcher]: MatchFn
  readonly [kind]: K
  readonly [narrowsTo]?: T
  readonly [writtenFor]?: (input: I) => void
}

type UnionM<T, I = any> = Matcher<'union', T, I>
type NotM<T, I = any> = Matcher<'not', T, I>
type GuardM<T> = Matcher<'guard', T>
type RefineM<T = unknown, I = any> = Matcher<'refine', T, I>
type NonNullableM = Matcher<'nonNullable'>
type AnyMatcher = { readonly [kind]: string }

type Primitive = string | number | boolean | bigint | symbol | null | undefined

// ---------- pattern shape ----------

// The patterns an input admits: its literal values, any matcher written against it, and
// for objects one merged shape over every union member's keys. Merging (rather than a union
// of per-member shapes) is what lets the compiler suggest the nearest literal ("Did you
// mean 'load'?") and reject unknown keys. It is also the contextual type that drives editor
// completion of keys and values.
// Written as a plain union (not a conditional) so compiler messages keep the alias name.
type KnownPattern<V> =
  | Matcher<string, unknown, V>
  | Extract<V, Primitive>
  | ArrayPattern<Extract<V, readonly unknown[]>>
  | ObjectPattern<Exclude<V, Primitive | readonly unknown[]>>
  | (unknown extends V ? unknown : never)

type ArrayPattern<A> = [A] extends [never] ? never : { readonly [i in keyof A]: KnownPattern<A[i]> }

type AllKeys<O> = O extends unknown ? keyof O : never
type ValueAt<O, k> = O extends unknown ? (k extends keyof O ? O[k] : never) : never
type ObjectPattern<O> = [O] extends [never]
  ? never
  : { readonly [k in AllKeys<O>]?: KnownPattern<ValueAt<O, k>> }

// ---------- narrowing ----------

type IsAny<V> = 0 extends 1 & V ? true : false

// The subset of V that pattern p can match. Distributes over V's union members. An `any`
// input (e.g. parsed JSON) stays `any`: no pattern can narrow or exhaust it.
type Narrow<V, p> = IsAny<V> extends true
  ? V
  : unknown extends V
  ? Invert<p>
  : p extends AnyMatcher
  ? NarrowMatcher<V, p>
  : p extends Primitive
  ? V extends unknown
    ? V extends p
      ? V
      : p extends V
      ? p
      : never
    : never
  : p extends readonly unknown[]
  ? V extends unknown
    ? V extends readonly (infer e)[]
      ? { -readonly [i in keyof p]: Narrow<e, p[i]> }
      : never
    : never
  : V extends unknown
  ? NarrowObject<V, p>
  : never

type NarrowGuard<V, g> = unknown extends V
  ? g
  : V extends unknown
  ? V extends g
    ? V
    : g extends V
    ? g
    : never
  : never

type NarrowMatcher<V, p> = p extends UnionM<infer u>
  ? Narrow<V, u>
  : p extends NotM<infer q>
  ? V extends unknown
    ? [V] extends [Narrow<V, q>]
      ? never
      : V
    : never
  : p extends GuardM<infer g> | RefineM<infer g>
  ? NarrowGuard<V, g>
  : p extends NonNullableM
  ? NonNullable<V>
  : V

type NarrowedFields<V, p> = {
  -readonly [k in keyof p]: k extends keyof V ? Narrow<V[k], p[k]> : never
}
type NeverField<f> = { [k in keyof f]: [f[k]] extends [never] ? k : never }[keyof f]
type ChangedField<V, f> = {
  [k in keyof f]: k extends keyof V ? ([V[k]] extends [f[k]] ? never : k) : k
}[keyof f]

// A member whose fields the pattern does not narrow is returned as-is, so handlers see the
// declared type rather than a structural copy.
type NarrowObject<V, p> = V extends object
  ? NarrowedFields<V, p> extends infer f
    ? [NeverField<f>] extends [never]
      ? [ChangedField<V, f>] extends [never]
        ? V
        : V & f
      : never
    : never
  : never

// ---------- exhaustiveness ----------

type ObjectPatterns<P> = P extends AnyMatcher
  ? P extends UnionM<infer u>
    ? ObjectPatterns<u>
    : never
  : P extends Primitive | readonly unknown[]
  ? never
  : P
type PatternKeys<P> = P extends unknown ? keyof P : never
type SubPatterns<P, k> = P extends unknown ? (k extends keyof P ? P[k] : never) : never

type UnionToIntersection<U> = (U extends unknown ? (x: U) => void : never) extends (
  x: infer I
) => void
  ? I
  : never
type LastOf<U> = UnionToIntersection<U extends unknown ? () => U : never> extends () => infer R
  ? R
  : never

// Split V into the cases the patterns P can tell apart: the cross product of the unions at
// every key some object pattern mentions, recursively. Keys no pattern mentions stay whole.
type DistributeBy<V, P> = [ObjectPatterns<P>] extends [never]
  ? V
  : V extends unknown
  ? V extends Primitive | readonly unknown[] | ((...args: never) => unknown)
    ? V
    : DistributeKeys<V, Extract<PatternKeys<ObjectPatterns<P>>, keyof V>, ObjectPatterns<P>>
  : never

type DistributeKeys<V, K, P> = [K] extends [never]
  ? V
  : LastOf<K> extends infer k extends keyof V
  ? DistributeBy<V[k], SubPatterns<P, k>> extends infer part
    ? part extends unknown
      ? DistributeKeys<{ [x in keyof V]: x extends k ? part : V[x] }, Exclude<K, k>, P>
      : never
    : never
  : never

type ContainsRefine<p> = p extends RefineM
  ? true
  : p extends AnyMatcher
  ? p extends UnionM<infer u> | NotM<infer u>
    ? ContainsRefine<u>
    : false
  : p extends Primitive
  ? false
  : true extends { [k in keyof p]: ContainsRefine<p[k]> }[keyof p]
  ? true
  : false

type Covers<m, p> = ContainsRefine<p> extends true
  ? false
  : [m] extends [Narrow<m, p>]
  ? true
  : false
type CoveredByAny<m, H> = true extends (H extends unknown ? Covers<m, H> : never) ? true : false

type Remaining<V, H> = DistributeBy<V, H> extends infer m
  ? m extends unknown
    ? CoveredByAny<m, H> extends true
      ? never
      : m
    : never
  : never

// Top-level exclusion only: drop the input members a pattern fully covers.
type ExcludeCovered<V, p> = IsAny<V> extends true
  ? V
  : V extends unknown
  ? Covers<V, p> extends true
    ? never
    : V
  : never

interface NonExhaustiveError<unhandled> {
  __nonExhaustive: never
  __unhandled: unhandled
}

// ---------- builder ----------

// Collapse `a | b` when one side absorbs the other, so `{}` does not widen a record result.
type Union<a, b> = [b] extends [a] ? a : [a] extends [b] ? b : a | b
type Output<O, R> = [O] extends [Unset] ? R : O
type HandlerOutput<O, c> = [O] extends [Unset] ? c : O

// A pattern that narrows the remaining input to nothing is a typo or a dead arm.
type MatchesNothing<V, p> = [Narrow<V, p>] extends [never]
  ? { __patternMatchesNothing: V }
  : unknown
interface PatternMatchesNoCaseOf<V> {
  __patternMatchesNoCaseOf: V
}

interface Match<V, O, H, R> {
  returnType<o>(): Match<V, o, H, R>
  with<const p extends KnownPattern<V>, c>(
    pattern: p,
    // Checked here rather than on `pattern`: a pattern parameter that mentions p itself
    // would stop nested `P.union`/`P.not` from inferring their input from it.
    handler: [Narrow<V, p>] extends [never]
      ? PatternMatchesNoCaseOf<V>
      : (value: Narrow<V, p>) => HandlerOutput<O, c>
  ): Match<ExcludeCovered<V, p>, O, H | p, Union<R, c>>
  // A guarded arm narrows like any other but never covers a case: the guard can reject any
  // value, so the input is not excluded and exhaustiveness ignores the pattern.
  with<const p extends KnownPattern<V>, c>(
    pattern: p,
    guard: (value: Narrow<V, p>) => unknown,
    handler: (value: Narrow<V, p>) => HandlerOutput<O, c>
  ): Match<V, O, H, Union<R, c>>
  otherwise<c>(handler: (value: V) => HandlerOutput<O, c>): Output<O, Union<R, c>>
  exhaustive: [Remaining<V, H>] extends [never]
    ? () => Output<O, R>
    : NonExhaustiveError<Remaining<V, H>>
  run(): Output<O, R>
}

type AnyHandler = (value: unknown) => unknown

function builder(value: unknown, matched: boolean, result: unknown) {
  const chain = {
    returnType: () => chain,
    with(pattern: unknown, ...rest: [AnyHandler] | [guard: AnyHandler, handler: AnyHandler]) {
      const handler = rest[rest.length - 1]
      if (matched || !matchPattern(pattern, value)) return chain
      if (rest.length == 2 && !rest[0](value)) return chain
      return builder(value, true, handler(value))
    },
    otherwise(handler: AnyHandler) {
      return matched ? result : handler(value)
    },
    exhaustive() {
      if (!matched) throw new Error('No pattern matches value', { cause: value })
      return result
    },
    run() {
      return chain.exhaustive()
    }
  }
  return chain
}

export function match<const V, O = Unset>(value: V): Match<V, O, never, never> {
  return builder(value, false, undefined) as unknown as Match<V, O, never, never>
}

// ---------- isMatching ----------

// The type a pattern matches on its own, for the one-argument form of `isMatching`.
type Invert<p> = p extends AnyMatcher
  ? p extends UnionM<infer u>
    ? Invert<u>
    : p extends GuardM<infer g> | RefineM<infer g>
    ? g
    : p extends NonNullableM
    ? {}
    : unknown
  : p extends Primitive
  ? p
  : { -readonly [k in keyof p]: Invert<p[k]> }

export function isMatching<const p>(pattern: p): (value: unknown) => value is Invert<p>
export function isMatching<const p, V>(pattern: p, value: V): value is Narrow<V, p> & V
export function isMatching(...args: [pattern: unknown] | [pattern: unknown, value: unknown]) {
  const pattern = args[0]
  if (args.length == 1) return (value: unknown) => matchPattern(pattern, value)
  return matchPattern(pattern, args[1])
}

// ---------- runtime matching ----------

function matchPattern(pattern: unknown, value: unknown): boolean {
  if (typeof pattern != 'object' || pattern == null) return Object.is(pattern, value)
  if (matcher in pattern) return (pattern as Matcher<string>)[matcher](value)
  if (typeof value != 'object' || value == null) return false
  if (Array.isArray(pattern)) {
    return (
      Array.isArray(value) &&
      value.length == pattern.length &&
      pattern.every((p, i) => matchPattern(p, value[i]))
    )
  }
  return Object.keys(pattern).every(
    (key) =>
      key in value &&
      matchPattern(
        (pattern as Record<string, unknown>)[key],
        (value as Record<string, unknown>)[key]
      )
  )
}

function build<M>(match: MatchFn): M {
  return { [matcher]: match } as M
}

// ---------- P ----------

export const P = {
  union<input, const ps extends readonly [KnownPattern<input>, ...KnownPattern<input>[]]>(
    ...patterns: ps
  ): UnionM<ps[number], input> {
    return build((value) => patterns.some((p) => matchPattern(p, value)))
  },
  not<input, const p extends KnownPattern<input>>(pattern: p): NotM<p, input> {
    return build((value) => !matchPattern(pattern, value))
  },
  when<input>(predicate: (value: input) => unknown): RefineM<unknown, input> {
    return build((value) => Boolean(predicate(value as input)))
  },
  string: build<GuardM<string>>((value) => typeof value == 'string'),
  number: Object.assign(
    build<GuardM<number>>((value) => typeof value == 'number'),
    {
      gt: (n: number): RefineM<number> => build((value) => typeof value == 'number' && value > n)
    }
  ),
  nullish: build<GuardM<null | undefined>>((value) => value == null),
  nonNullable: build<NonNullableM>((value) => value != null),
  _: build<GuardM<unknown>>(() => true)
}

export declare namespace P {
  type Pattern<V> = KnownPattern<V>
  type valid<V, p> = p & MatchesNothing<V, p>
  type narrow<V, p> = Narrow<V, p>
}
